/**
 * FormResponsesListPage — registre global des réponses (9.U.7).
 *
 * Route `/reponses`. Liste tenant-scopée (métadonnées seules), filtres pilotés
 * par la query string (partageables ; deep-link `?template_id=…` depuis une fiche
 * formulaire), pagination keyset, export CSV des filtres actifs. Une ligne mène
 * à l'`intake_event` (viewer 1e-A) quand il existe, sinon ouvre un fallback
 * read-only réutilisant `IntakeFormResponsePanel`. Aucune mutation ni action de
 * masse. Human-only (imposé côté API).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { apiDownload, ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  formResponsesCsvPath,
  useFormResponses,
  type FormResponseListItem,
  type FormResponseListParams,
} from "../../lib/queries/formResponses";
import { toast } from "../../lib/toast";
import { IntakeFormResponsePanel } from "../intake-events/_parts/IntakeFormResponsePanel";

import { FormResponsesFilters } from "./_parts/FormResponsesFilters";

const FILTER_KEYS = [
  "status",
  "submitted_from",
  "template_id",
  "template_version_id",
  "project_id",
  "intake_link_id",
  "from",
  "to",
] as const;

function statusBadgeClass(status: string): string {
  switch (status) {
    case "submitted":
      return "bg-brand-50 text-brand-700 ring-brand-200";
    case "locked":
      return "bg-slate-100 text-slate-600 ring-slate-300";
    default:
      return "bg-amber-50 text-amber-700 ring-amber-200"; // draft
  }
}

export function FormResponsesListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePagination();
  const [drawerResponseId, setDrawerResponseId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filtres = query string (source de vérité). `intake_link_id` est param-only
  // (deep-linkable, pas de contrôle dédié).
  const filters: FormResponseListParams = {};
  for (const k of FILTER_KEYS) {
    const v = searchParams.get(k);
    if (v) (filters as Record<string, string>)[k] = v;
  }
  const filterKey = FILTER_KEYS.map((k) => searchParams.get(k) ?? "").join("|");

  // Reset pagination à chaque changement de filtres.
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      pagination.reset();
    }
  }, [filterKey, pagination]);

  const { data, isLoading, isFetching, error, refetch } = useFormResponses({
    ...filters,
    cursor: pagination.cursor ?? undefined,
  });

  const onFilterChange = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSearchParams(next, { replace: true });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const filename = `form-responses-${new Date().toISOString().slice(0, 10)}.csv`;
      const { truncated } = await apiDownload(formResponsesCsvPath(filters), filename);
      if (truncated) toast.info(t("form_responses.export.truncated"));
      else toast.success(t("form_responses.export.done"));
    } catch (err) {
      toast.error(
        err instanceof ApiException ? getApiErrorMessage(err, lang) : t("errors.generic"),
      );
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {filters.project_id && (
        <Link
          to={`/projects/${filters.project_id}#quote-requests`}
          className="focus-visible:ring-brand-500 text-brand-700 inline-flex min-h-11 items-center rounded-md px-2 text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("prepare_send.back_to_project")}
        </Link>
      )}
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t("form_responses.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("form_responses.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => void onExport()}
          disabled={exporting || isLoading}
          className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          {exporting ? t("form_responses.export.exporting") : t("form_responses.export.button")}
        </button>
      </header>

      <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <FormResponsesFilters value={filters} onChange={onFilterChange} />
      </section>

      {/* States : loading / error / empty / data */}
      {isLoading ? (
        <div
          className="mt-6 space-y-3"
          role="status"
          aria-live="polite"
          aria-label={t("app.loading")}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-700"
        >
          <p>
            {error instanceof ApiException ? getApiErrorMessage(error, lang) : t("errors.generic")}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            {t("form_responses.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
          {t("form_responses.empty")}
        </p>
      ) : (
        <>
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{t("form_responses.col.date")}</th>
                  <th className="px-4 py-3">{t("form_responses.col.source")}</th>
                  <th className="px-4 py-3">{t("form_responses.col.status")}</th>
                  <th className="px-4 py-3">{t("form_responses.col.form")}</th>
                  <th className="px-4 py-3">{t("form_responses.col.project")}</th>
                  <th className="px-4 py-3">{t("form_responses.col.processing")}</th>
                  <th className="px-4 py-3 text-right">{t("form_responses.col.detail")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <ResponseRow
                    key={r.id}
                    row={r}
                    lang={lang}
                    onOpenFallback={() => setDrawerResponseId(r.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Pagination
              pageNumber={pagination.pageNumber}
              hasPrev={pagination.hasPrev}
              hasNext={!!data?.next_cursor}
              onPrev={pagination.prev}
              onNext={() => pagination.next(data?.next_cursor ?? null)}
              disabled={isFetching}
            />
          </div>
        </>
      )}

      {drawerResponseId && (
        <ResponseDrawer id={drawerResponseId} onClose={() => setDrawerResponseId(null)} />
      )}
    </div>
  );
}

function ResponseRow({
  row,
  lang,
  onOpenFallback,
}: {
  row: FormResponseListItem;
  lang: "fr" | "en";
  onOpenFallback: () => void;
}) {
  const { t } = useTranslation();
  const date = row.submitted_at ?? row.created_at;
  const dateStr = new Date(date).toLocaleString(lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{dateStr}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          {row.submitted_from
            ? t(`form_responses.source.${row.submitted_from}`)
            : t("form_responses.source.unknown")}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusBadgeClass(row.status)}`}
        >
          {t(`form_responses.status.${row.status}`)}
        </span>
      </td>
      <td className="px-4 py-3 text-slate-700">
        <span className="font-medium text-slate-900">{row.template_name}</span>{" "}
        <span className="text-slate-400">
          {t("form_responses.filters.version_option", { number: row.version_number })}
        </span>
      </td>
      <td className="px-4 py-3">
        {row.project_id ? (
          <Link
            to={`/projects/${row.project_id}`}
            className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            {row.project_title ?? row.project_reference ?? t("form_responses.col.project")}
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600">
        {row.processing_status
          ? t(`form_responses.processing.${row.processing_status}`, {
              defaultValue: row.processing_status,
            })
          : t("form_responses.processing.untreated")}
      </td>
      <td className="px-4 py-3 text-right">
        {row.intake_event_id ? (
          <Link
            to={`/intake-events/${row.intake_event_id}`}
            className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            {t("form_responses.open_intake")}
          </Link>
        ) : (
          <button
            type="button"
            onClick={onOpenFallback}
            className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            {t("form_responses.view_response")}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Fallback read-only pour une réponse SANS intake_event : réutilise
 * `IntakeFormResponsePanel` (loading/success/unavailable + FormRenderer readonly)
 * dans un panneau latéral. Aucune duplication de la logique de chargement.
 */
function ResponseDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/30"
      role="dialog"
      aria-modal="true"
      aria-label={t("form_responses.drawer.title")}
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-xl overflow-y-auto bg-slate-50 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {t("form_responses.drawer.title")}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="focus-visible:ring-brand-500 rounded-md p-1.5 text-slate-500 hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2"
            aria-label={t("form_responses.drawer.close")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <IntakeFormResponsePanel formResponseId={id} />
      </div>
    </div>
  );
}
